import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import type { PerfLiveSnapshot, RunStatus } from '@1qa/shared';
import { Activity, Gauge, Loader2, WifiOff } from '@/components/icons';
import { openLiveStream } from '@/lib/api';
import { RECONNECT_DELAYS, parseLiveEvent } from '@/lib/liveView';
import { readSse } from '@/lib/sse';
import { formatMinutes, formatMs, formatNumber, formatPct } from '@/lib/perf';
import { Badge, Card, CardBody, CardHeader } from '@/components/ui';
import { TimeChart } from './TimeChart';

/** Keep about 30 minutes of 5 s points for the live charts. */
const MAX_POINTS = 360;

/**
 * Live dashboard of a performance run: the runner publishes a snapshot of the newest 5 s of every agent together
 * (LiveEvent `perf`), the API relays it over SSE (GET /runs/:id/live), this draws it.
 */
export function PerfLive({ runId, runStatus, onEnd }: { runId: string; runStatus: RunStatus; onEnd: () => void }) {
  const [snap, setSnap] = useState<PerfLiveSnapshot | null>(null);
  const [points, setPoints] = useState<{ t: number; rps: number; p95: number | null; vus: number; err: number }[]>([]);
  const [conn, setConn] = useState<'connecting' | 'live' | 'retrying' | 'lost'>('connecting');
  const [generation, setGeneration] = useState(0);
  const attempt = useRef(0);
  const ended = useRef(false);
  const onEndRef = useRef(onEnd);
  onEndRef.current = onEnd;

  useEffect(() => {
    if (ended.current) return;
    const ctrl = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    (async () => {
      try {
        const res = await openLiveStream(runId, ctrl.signal);
        setConn('live');
        attempt.current = 0;
        await readSse(
          res.body!,
          (msg) => {
            const ev = parseLiveEvent(msg.data);
            if (!ev) return;
            if (ev.type === 'perf') {
              setSnap(ev);
              if (ev.phase !== 'starting') {
                setPoints((p) => {
                  const point = { t: ev.elapsedS, rps: ev.rps, p95: ev.p95Ms, vus: ev.vus, err: ev.errorRate * 100 };
                  const next = p.length && p[p.length - 1]!.t === point.t ? [...p.slice(0, -1), point] : [...p, point];
                  return next.slice(-MAX_POINTS);
                });
              }
            } else if (ev.type === 'end') {
              ended.current = true;
              onEndRef.current();
            }
          },
          ctrl.signal,
        );
        if (ctrl.signal.aborted || ended.current) return;
        throw new Error('stream closed');
      } catch {
        if (ctrl.signal.aborted || ended.current) return;
        const delay = RECONNECT_DELAYS[attempt.current];
        if (delay === undefined) return setConn('lost');
        setConn('retrying');
        timer = setTimeout(() => {
          attempt.current += 1;
          setGeneration((g) => g + 1);
        }, delay);
      }
    })();
    return () => {
      ctrl.abort();
      if (timer) clearTimeout(timer);
    };
  }, [runId, generation]);

  const progress = snap && snap.plannedS ? Math.min(1, snap.elapsedS / snap.plannedS) : 0;
  const phaseText =
    runStatus === 'QUEUED' ? 'Waiting for a runner…' : !snap || snap.phase === 'starting' ? 'Getting the load agents ready…' : snap.phase === 'finishing' ? 'Finishing: the last users complete their journey…' : 'Load is running';

  return (
    <Card data-testid="perf-live">
      <CardHeader
        title={
          <span className="inline-flex items-center gap-2">
            <Activity className="size-4 text-primary" aria-hidden /> Live
          </span>
        }
        description={phaseText}
        actions={
          conn === 'live' ? (
            <Badge tone="success">Connected</Badge>
          ) : conn === 'lost' ? (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 text-xs text-link hover:underline"
              onClick={() => {
                attempt.current = 0;
                setConn('connecting');
                setGeneration((g) => g + 1);
              }}
            >
              <WifiOff className="size-3.5" /> Reconnect
            </button>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs text-fg-muted">
              <Loader2 className="size-3.5 animate-spin" /> {conn === 'retrying' ? 'Reconnecting…' : 'Connecting…'}
            </span>
          )
        }
      />
      <CardBody className="space-y-5">
        <div>
          <div className="mb-1 flex justify-between text-xs text-fg-muted tabular-nums">
            <span>{snap ? formatMinutes(snap.elapsedS) : '0 s'}</span>
            <span>{snap ? `of ${formatMinutes(snap.plannedS)}` : ''}</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-surface-sunken" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress * 100)} aria-label="Run progress">
            <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${progress * 100}%` }} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5" aria-live="polite">
          {[
            ['Users', snap ? formatNumber(snap.vus) : '-'],
            ['Requests / s', snap ? formatNumber(snap.rps) : '-'],
            ['p95', formatMs(snap?.p95Ms)],
            ['p99', formatMs(snap?.p99Ms)],
            ['Errors', snap ? formatPct(snap.errorRate) : '-'],
          ].map(([label, value]) => (
            <div key={label} className="rounded-card bg-surface-sunken px-4 py-3 ring-1 ring-border-subtle ring-inset">
              <div className="text-xs font-medium tracking-label text-fg-muted uppercase">{label}</div>
              <div className={clsx('mt-1 text-xl font-semibold tabular-nums', label === 'Errors' && snap && snap.errorRate > 0 ? 'text-danger-text' : 'text-fg')}>{value}</div>
            </div>
          ))}
        </div>
        {points.length > 1 && (
          <div className="grid gap-6 lg:grid-cols-2">
            <TimeChart title="Response time (p95)" series={[{ label: 'p95', color: 'text-primary', values: points.map((p) => p.p95) }]} format={(v) => formatMs(v)} />
            <TimeChart title="Requests per second" series={[{ label: 'requests / s', color: 'text-primary', values: points.map((p) => p.rps) }]} format={(v) => formatNumber(v)} />
          </div>
        )}
        {snap && snap.transactions.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <caption className="sr-only">Transactions in the last 5 seconds</caption>
              <thead className="text-xs tracking-label text-fg-muted uppercase">
                <tr>
                  <th className="py-1 text-left font-medium">Transaction (last 5 s)</th>
                  <th className="py-1 text-right font-medium">Requests</th>
                  <th className="py-1 text-right font-medium">p95</th>
                  <th className="py-1 text-right font-medium">Failed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-subtle">
                {snap.transactions.map((t) => (
                  <tr key={t.name}>
                    <td className="py-1.5 text-fg">{t.name}</td>
                    <td className="py-1.5 text-right tabular-nums">{formatNumber(t.requests)}</td>
                    <td className="py-1.5 text-right tabular-nums">{formatMs(t.p95Ms)}</td>
                    <td className={clsx('py-1.5 text-right tabular-nums', t.failed > 0 && 'text-danger-text')}>{formatNumber(t.failed)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {snap && (
          <ul className="flex flex-wrap gap-2" aria-label="Load agents">
            {snap.agents.map((a) => (
              <li key={a.id} className="inline-flex items-center gap-2 rounded-full bg-surface-sunken px-3 py-1 text-xs ring-1 ring-border-subtle ring-inset">
                <Gauge className="size-3.5 text-fg-muted" aria-hidden />
                <span className="font-medium text-fg">{a.name}</span>
                <Badge tone={a.status === 'running' ? 'success' : a.status === 'lost' ? 'warning' : a.status === 'done' ? 'neutral' : 'pending'}>
                  {a.status === 'running' ? 'Running' : a.status === 'lost' ? 'Lost' : a.status === 'done' ? 'Done' : 'Waiting'}
                </Badge>
                {a.status === 'running' && <span className="text-fg-muted tabular-nums">CPU {a.cores.toFixed(1)} cores</span>}
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}
